/**
 * Practice Performance Metrics Page
 * Displays practice-wide performance analytics
 */

import { useState, useEffect } from 'react'
import { useOrganizationId } from '../hooks/useOrganizationId'
import Card from '../components/Card'
import Button from '../components/Button'
import { BarChart, LineChart, PieChart } from '../components/Chart'
import {
  TrendingUp,
  Users,
  FileText,
  Heart,
  Calendar,
  Target,
  Activity,
  RefreshCw,
} from 'lucide-react'
import { getPracticeMetrics, getPerformanceTrends } from '../services/practicePerformanceMetrics'

const PracticePerformance = () => {
  const organizationId = useOrganizationId()
  const [metrics, setMetrics] = useState(null)
  const [trends, setTrends] = useState(null)
  const [timeRange, setTimeRange] = useState(30)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (organizationId) {
      loadData()
    }
  }, [organizationId, timeRange])

  const loadData = async () => {
    setLoading(true)
    try {
      const [metricsData, trendsData] = await Promise.all([
        getPracticeMetrics(organizationId, timeRange),
        getPerformanceTrends(organizationId, 90),
      ])

      if (metricsData.success) {
        setMetrics(metricsData.data)
      }
      if (trendsData.success) {
        setTrends(trendsData.data)
      }
    } catch (error) {
      console.error('Error loading performance data:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="section-container">
        <div className="text-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading performance metrics...</p>
        </div>
      </div>
    )
  }

  if (!metrics) {
    return (
      <div className="section-container">
        <p className="text-gray-500">No performance data available</p>
      </div>
    )
  }

  return (
    <div className="section-container space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <Activity className="w-8 h-8" />
          Practice Performance Metrics
        </h1>
        <div className="flex gap-2">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(parseInt(e.target.value))}
            className="px-4 py-2 border rounded-lg"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <Button onClick={loadData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">New Clients</p>
              <p className="text-3xl font-bold">{metrics.newClients}</p>
            </div>
            <Users className="w-8 h-8 text-primary-500" />
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Care Plan Completion</p>
              <p className="text-3xl font-bold">{metrics.carePlanCompletionRate}%</p>
            </div>
            <Target className="w-8 h-8 text-green-500" />
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Therapy Completion</p>
              <p className="text-3xl font-bold">{metrics.therapyCompletionRate}%</p>
            </div>
            <Calendar className="w-8 h-8 text-blue-500" />
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Client Retention</p>
              <p className="text-3xl font-bold">{metrics.clientRetentionRate}%</p>
            </div>
            <TrendingUp className="w-8 h-8 text-purple-500" />
          </div>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Activity Trends */}
        {trends && trends.length > 0 && (
          <Card>
            <h2 className="text-xl font-semibold mb-4">Activity Trends (90 days)</h2>
            <LineChart
              data={trends.map((t) => ({
                date: new Date(t.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                clients: t.clients,
                examinations: t.examinations,
                sessions: t.sessions,
              }))}
              xKey="date"
              yKey="sessions"
              title="Sessions Over Time"
            />
          </Card>
        )}

        {/* Care Plan Status */}
        <Card>
          <h2 className="text-xl font-semibold mb-4">Care Plan Status</h2>
          <PieChart
            data={[
              { status: 'Active', count: metrics.activeCarePlans },
              { status: 'Completed', count: metrics.completedCarePlans },
              { status: 'Total', count: metrics.carePlansCount },
            ]}
            labelKey="status"
            valueKey="count"
          />
        </Card>
      </div>

      {/* Detailed Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <h2 className="text-xl font-semibold mb-4">Engagement Metrics</h2>
          <div className="space-y-4">
            <div className="flex justify-between">
              <span className="text-gray-600">Total Clients</span>
              <span className="font-semibold">{metrics.totalClients}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Active Clients</span>
              <span className="font-semibold">{metrics.activeClients}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Examinations per Client</span>
              <span className="font-semibold">{metrics.examinationsPerClient}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Average Session Duration</span>
              <span className="font-semibold">{metrics.averageSessionDuration} min</span>
            </div>
          </div>
        </Card>

        <Card>
          <h2 className="text-xl font-semibold mb-4">Outcome Metrics</h2>
          <div className="space-y-4">
            <div className="flex justify-between">
              <span className="text-gray-600">Outcomes Recorded</span>
              <span className="font-semibold">{metrics.outcomesRecorded}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Average Improvement</span>
              <span className="font-semibold">{metrics.averageOutcomeImprovement}%</span>
            </div>
          </div>
        </Card>
      </div>
    </div>
  )
}

export default PracticePerformance

